import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Button, Input } from '@components/common';

import { isEmpty } from '@utils/common';

import { IFolderFormValues } from '@interfaces/components/form';
import { ICheckFolderNameDTO, ICreateFolderDTO, IRenameFolderDTO } from '@interfaces/portal/dto/foldersManagementDTO';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { MAX_70_LENGTH } from '@constants/validationMessages';
import { folderFormFields } from './constants';

import { getSchema } from './schema';

import styles from './FolderForm.module.scss';

interface IFolderFormProps {
	projectId: string;
	closeFolderForm: () => void;
	parentFolderIdForCreate: string | null;
	createFolder: (data: ICreateFolderDTO) => Promise<void>;
	renameFolder: (data: IRenameFolderDTO) => Promise<void>;
	handleCheckFolderName: (data: ICheckFolderNameDTO) => Promise<void>;
	folderForRename?: IRenameFolderDTO | null;
	isEditMode: boolean;
}

const FolderForm = ({
	projectId,
	closeFolderForm,
	parentFolderIdForCreate,
	createFolder,
	handleCheckFolderName,
	folderForRename,
	isEditMode,
	renameFolder,
}: IFolderFormProps) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		watch,
	} = useForm<IFolderFormValues>({
		resolver: yupResolver(getSchema({
			projectId,
			parentFolderId: folderForRename ? folderForRename.parentFolderId : parentFolderIdForCreate,
			handleCheckFolderName,
			folderId: folderForRename ? folderForRename.folderId : null,
		})),
		mode: EValidationMode.onSubmit,
		reValidateMode: ERevalidationMode.onChange,
		defaultValues: {
			folderName: folderForRename?.folderName,
		},
	});

	const folderName = watch(folderFormFields.folderName)?.trim();
	const folderNameLength = folderName?.length || 0;

	const onSubmit = (values: IFolderFormValues) => {
		if (isEditMode && folderForRename) {
			void renameFolder({
				folderName: values.folderName,
				folderId: folderForRename.folderId,
				isParentFolder: folderForRename.isParentFolder,
				parentFolderId: folderForRename.parentFolderId,
			});
		} else {
			void createFolder({
				projectId,
				name: values.folderName,
				parentFolderId: parentFolderIdForCreate,
			});
		}

		closeFolderForm();
	};

	const isSaveDisabled = !isEmpty(errors) || folderForRename?.folderName === folderName;

	return (
		<>
			<div className={styles.folderName}>
				<Input
					errorMessage={errors?.folderName?.message}
					isError={!!errors?.folderName}
					isMaxLengthCounter
					label='Folder Name'
					maxLength={MAX_70_LENGTH}
					name={folderFormFields.folderName}
					placeholder='Enter folder name'
					register={register}
					required
					setValue={setValue}
					valueLength={folderNameLength}
				/>
			</div>
			<div className={styles.buttons}>
				<Button
					onClick={closeFolderForm}
					text='Cancel'
					withoutBackground
				/>
				<Button
					disabled={isSaveDisabled}
					onClick={handleSubmit(onSubmit)}
					text='Save'
				/>
			</div>
		</>
	);
};

export default FolderForm;
